import { useEffect } from 'react';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded';
import mqtt from "mqtt";

const MQTT_OPTIONS = {
    username: 'operator_ws',
    password: 'Q4ddoje88D_u1cUsTUHwI30dtbwdj6LP',
    rejectUnauthorized: true,
    reconnectPeriod: 2000,
};

const client = mqtt.connect('wss://ivm9.in:9001', MQTT_OPTIONS);

export default function RightGridTable({ rows, setQuantity, deleteRow, TAX_RATE, invoiceSubtotal, invoiceTaxes, invoiceTotal }) {
    const on_call_vm = rows[0]?.vmId;

    useEffect(() => {
        if (!on_call_vm || rows.length === 0) {
            return;
        }
        client.publish(`vm/pi/local/${on_call_vm}`, JSON.stringify(rows));
    }, [on_call_vm, rows]);

    const ccyFormat = (num) => `${Number(num || 0).toFixed(2)}`;

    const summaryRows = [
        { label: 'Subtotal', value: ccyFormat(invoiceSubtotal), tone: 'default' },
        { label: `Tax (${(TAX_RATE * 100).toFixed(0)}%)`, value: ccyFormat(invoiceTaxes), tone: 'default' },
        { label: 'Total', value: ccyFormat(invoiceTotal), tone: 'strong' },
    ];

    return (
        <Paper
            elevation={0}
            sx={(theme) => ({
                height: '100%',
                borderRadius: 2,
                border: '1px solid',
                borderColor: alpha(theme.palette.divider, 0.8),
                bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.92)
                    : '#fbfbfc',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
            })}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={(theme) => ({
                    px: { xs: 1.5, md: 2.25 },
                    py: 1.5,
                    borderBottom: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.7),
                    background: theme.palette.mode === 'dark'
                        ? `linear-gradient(135deg, ${alpha(theme.palette.info.dark, 0.18)} 0%, ${alpha(theme.palette.background.paper, 0)} 70%)`
                        : 'linear-gradient(135deg, rgba(25,118,210,0.08) 0%, rgba(255,255,255,0) 70%)',
                })}
            >
                <Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <ReceiptLongRoundedIcon color="primary" />
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                            Checkout Summary
                        </Typography>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                        Review selected products, adjust quantity, and confirm the final bill.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        icon={<Inventory2RoundedIcon />}
                        label={`${rows.length} ${rows.length === 1 ? 'item' : 'items'}`}
                        color="primary"
                        variant="outlined"
                        sx={{ borderRadius: 2.5, fontWeight: 700 }}
                    />
                </Stack>
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <TableContainer sx={{ minWidth: 0 }}>
                    <Table size="small" aria-label="checkout table">
                        <TableHead>
                            <TableRow
                                sx={(theme) => ({
                                    '& th': {
                                        borderBottom: '1px solid',
                                        borderColor: alpha(theme.palette.divider, 0.8),
                                        bgcolor: alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.2 : 0.45),
                                        color: theme.palette.text.secondary,
                                        fontSize: '0.74rem',
                                        fontWeight: 800,
                                        letterSpacing: '0.08em',
                                        textTransform: 'uppercase',
                                        py: 1.25,
                                    },
                                })}
                            >
                                <TableCell>Item</TableCell>
                                <TableCell align="center">Stock</TableCell>
                                <TableCell align="center">Qty</TableCell>
                                <TableCell align="right">Unit</TableCell>
                                <TableCell align="right">Line Total</TableCell>
                                <TableCell align="center">Remove</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                                        <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                            No items selected
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            Search and add products from the active machine to start a checkout.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )}

                            {rows.map((row, index) => (
                                <TableRow
                                    key={`${row.itemId}-${row.slotId}-${index}`}
                                    sx={(theme) => ({
                                        '& td': {
                                            borderBottom: '1px solid',
                                            borderColor: alpha(theme.palette.divider, 0.55),
                                            py: 1.15,
                                        },
                                        '&:hover': {
                                            bgcolor: alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.08 : 0.03),
                                        },
                                    })}
                                >
                                    <TableCell>
                                        <Stack spacing={0.35}>
                                            <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                                {row.desc}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {row.slotIdsLabel || `Slot ${row.slotId}`}
                                            </Typography>
                                        </Stack>
                                    </TableCell>
                                    <TableCell align="center">
                                        <Chip
                                            size="small"
                                            label={row.stock}
                                            color={row.stock > 0 ? 'success' : 'default'}
                                            variant={row.stock > 0 ? 'filled' : 'outlined'}
                                            sx={{ minWidth: 58, fontWeight: 700, borderRadius: 2 }}
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <TextField
                                            type="number"
                                            size="small"
                                            value={rows[index].qty}
                                            onChange={(e) => setQuantity(e, index)}
                                            inputProps={{ min: 0, style: { textAlign: 'center', padding: '8px 6px' } }}
                                            sx={{
                                                width: 74,
                                                '& .MuiOutlinedInput-root': {
                                                    borderRadius: 2.5,
                                                    bgcolor: 'background.paper',
                                                },
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{ fontWeight: 600 }}>
                                            {ccyFormat(row.unit)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        <Typography sx={{ fontWeight: 800 }}>
                                            {ccyFormat(row.price)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton
                                            aria-label="delete"
                                            color="error"
                                            size="small"
                                            onClick={(e) => deleteRow(e, index)}
                                            sx={(theme) => ({
                                                border: '1px solid',
                                                borderColor: alpha(theme.palette.error.main, 0.28),
                                                bgcolor: alpha(theme.palette.error.main, 0.08),
                                            })}
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small" />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            <Box
                sx={(theme) => ({
                    borderTop: '1px solid',
                    borderColor: alpha(theme.palette.divider, 0.75),
                    px: { xs: 1.5, md: 2.25 },
                    py: 1.5,
                    bgcolor: alpha(theme.palette.action.hover, theme.palette.mode === 'dark' ? 0.16 : 0.4),
                })}
            >
                <Stack spacing={1}>
                    {summaryRows.map((item) => (
                        <Stack key={item.label} direction="row" justifyContent="space-between" alignItems="center">
                            <Typography
                                variant={item.tone === 'strong' ? 'subtitle1' : 'body2'}
                                color={item.tone === 'strong' ? 'text.primary' : 'text.secondary'}
                                sx={{ fontWeight: item.tone === 'strong' ? 800 : 600 }}
                            >
                                {item.label}
                            </Typography>
                            <Typography
                                variant={item.tone === 'strong' ? 'h6' : 'body1'}
                                sx={{ fontWeight: item.tone === 'strong' ? 800 : 700 }}
                            >
                                {item.value}
                            </Typography>
                        </Stack>
                    ))}
                </Stack>
            </Box>
        </Paper>
    );
}
